<!-- eslint-disable @intlify/vue-i18n/no-dynamic-keys -->
<script setup lang="ts">
import { useProductNoun } from "@/composition/useProductNoun";
import { useAppStore } from "@/store/app";
import {
  EMBED_LAYOUT_TYPES,
  type EmbedLayoutId,
  type IntegratePane,
} from "@/util/embedCode";
import { setPostSavePanelHidden } from "@/util/userPreferences";
import {
  ChevronRightIcon,
  XCloseIcon,
  ZapIcon,
} from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UICheckbox } from "@gohighlevel/ghl-ui";
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import EmbedTypeThumbnail from "./EmbedTypeThumbnail.vue";
import SharePanel from "./SharePanel.vue";

const store = useAppStore();
const { t } = useI18n();
const { assetParams } = useProductNoun();
const panel = ref<HTMLElement>();
const heading = ref<HTMLElement>();
const hideNextTime = ref(false);
const anchorTop = ref(48);
const overlayZIndex = ref<number>();
const dropdownStyle = computed(() => ({
  top: `${anchorTop.value}px`,
  maxHeight: `calc(100vh - ${anchorTop.value}px - 16px)`,
  zIndex: overlayZIndex.value,
}));
const layouts = computed<
  Array<{ id: EmbedLayoutId; key: string; label: string }>
>(() =>
  EMBED_LAYOUT_TYPES.filter(({ id }) => !!store.formId || id === "INLINE").map(
    ({ id, key, labelKey }) => ({
      id,
      key,
      label: t(labelKey),
    })
  )
);

const persistHidePreference = () => {
  if (hideNextTime.value) setPostSavePanelHidden(true);
};
const dismiss = (restoreFocus: boolean) => {
  persistHidePreference();
  store.showPostSavePanel = false;
  if (restoreFocus) document.getElementById("save-action")?.focus();
};
const close = () => dismiss(true);
const openExternalLink = () => dismiss(false);
const openPane = (pane: IntegratePane, layoutId?: EmbedLayoutId) => {
  persistHidePreference();
  store.openIntegratePane(pane, layoutId);
};
const onKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape") {
    close();
    return;
  }
  if (event.key !== "Tab" || !panel.value) return;
  const focusable = Array.from(
    panel.value.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((element) => element.tabIndex >= 0);
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!first || !last) return;
  if (
    event.shiftKey &&
    (document.activeElement === first ||
      !panel.value.contains(document.activeElement))
  ) {
    event.preventDefault();
    last.focus();
  } else if (
    !event.shiftKey &&
    (document.activeElement === last ||
      !panel.value.contains(document.activeElement))
  ) {
    event.preventDefault();
    first.focus();
  }
};
const onPointerdown = (event: PointerEvent) => {
  if (event.target instanceof Node && !panel.value?.contains(event.target))
    close();
};
const updateAnchor = () => {
  const saveButton = document.getElementById("save-action");
  if (saveButton)
    anchorTop.value = saveButton.getBoundingClientRect().bottom + 4;
};
const updateOverlayLayer = () => {
  const layers = document.querySelectorAll<HTMLElement>(
    "#builder-header, .n-drawer-container"
  );
  const highestLayer = Array.from(layers).reduce(
    (highest, layer) =>
      Math.max(highest, Number(getComputedStyle(layer).zIndex) || 0),
    0
  );
  overlayZIndex.value = highestLayer + 1;
};
onMounted(() => {
  updateAnchor();
  updateOverlayLayer();
  nextTick(() => heading.value?.focus());
  window.addEventListener("keydown", onKeydown);
  document.addEventListener("pointerdown", onPointerdown);
  window.addEventListener("resize", updateAnchor);
});
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
  document.removeEventListener("pointerdown", onPointerdown);
  window.removeEventListener("resize", updateAnchor);
});
</script>

<template>
  <Teleport to="body">
    <aside
      ref="panel"
      data-testid="post-save-panel"
      class="post-save-panel post-save-panel--dropdown"
      :style="dropdownStyle"
    >
      <header class="post-save-panel__header">
        <div>
          <h2 ref="heading" data-testid="post-save-heading" tabindex="-1">
            {{ t("integrate.postSave.title", assetParams) }}
          </h2>
          <p>{{ t("integrate.postSave.description") }}</p>
        </div>
        <UIButton
          id="post-save-close"
          data-testid="post-save-close"
          class="post-save-panel__close integrate-close-button"
          text
          :aria-label="t('common.close')"
          @click="close"
        >
          <XCloseIcon />
        </UIButton>
      </header>

      <div
        data-testid="post-save-body"
        class="post-save-panel__body post-save-panel__body--scrollable"
      >
        <section class="post-save-panel__section">
          <h3>{{ t("common.share") }}</h3>
          <SharePanel
            :url="store.integrateShareUrl"
            compact
            @open-link="openExternalLink"
            @open-email="openPane('email')"
            @open-qr="openPane('share')"
          />
        </section>

        <section class="post-save-panel__section">
          <h3>{{ t("integrate.postSave.embedForm", assetParams) }}</h3>
          <div class="post-save-panel__layouts">
            <UIButton
              v-for="layout in layouts"
              :key="layout.id"
              :id="`post-save-embed-${layout.key}`"
              :data-testid="`post-save-embed-${layout.key}`"
              class="post-save-panel__layout"
              text
              @click="openPane('embed', layout.id)"
            >
              <EmbedTypeThumbnail :type="layout.key" compact />
              <span class="post-save-panel__layout-label">{{
                layout.label
              }}</span>
            </UIButton>
          </div>
        </section>

        <section
          v-if="store.canOpenWorkflowHotlinks"
          class="post-save-panel__section"
        >
          <h3>{{ t("integrate.postSave.connect") }}</h3>
          <UIButton
            id="post-save-workflow"
            data-testid="post-save-workflow"
            text
            class="post-save-panel__workflow"
            :aria-label="t('integrate.postSave.createWorkflow')"
            @click="openPane('connections')"
          >
            <span class="post-save-panel__workflow-icon"><ZapIcon /></span>
            <span class="post-save-panel__workflow-copy">
              <span class="post-save-panel__workflow-title">
                {{ t("integrate.postSave.createWorkflow") }}
              </span>
              <span class="post-save-panel__workflow-subtitle">
                {{ t("integrate.postSave.workflowDescription") }}
              </span>
            </span>
            <ChevronRightIcon class="post-save-panel__workflow-chevron" />
          </UIButton>
        </section>
      </div>

      <footer class="post-save-panel__footer">
        <UICheckbox
          id="post-save-hide"
          :checked="hideNextTime"
          @update:checked="hideNextTime = $event"
        >{{ t("integrate.postSave.dontShowAgain") }}</UICheckbox>
      </footer>
    </aside>
  </Teleport>
</template>

<style scoped>
.post-save-panel {
  position: fixed;
  z-index: 1001;
  top: 48px;
  right: clamp(12px, 1.11vw, 24px);
  display: flex;
  width: clamp(400px, 35.56vw, 600px);
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 64px);
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid var(--gray-200, #eaecf0);
  border-radius: 12px;
  background: var(--base-white, #fff);
  box-shadow: 0 20px 24px -4px rgb(16 24 40 / 8%),
    0 8px 8px -4px rgb(16 24 40 / 3%);
}
.post-save-panel__header {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid var(--gray-200, #eaecf0);
}
.post-save-panel__header > div {
  min-width: 0;
  flex: 1;
}
.post-save-panel__header h2 {
  margin: 0;
  color: var(--gray-900, #101828);
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
}
.post-save-panel__header p {
  margin: 2px 0 0;
  color: var(--gray-600, #475467);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  overflow-wrap: anywhere;
}
.post-save-panel__close {
  --integrate-close-color: var(--gray-600, #475467);
  width: 24px;
  min-width: 24px;
  height: 24px;
  padding: 0 !important;
}
.post-save-panel__close svg {
  width: 24px;
  height: 24px;
}
.post-save-panel__body {
  display: flex;
  min-height: 0;
  flex: 0 1 auto;
  flex-direction: column;
  gap: 20px;
  padding: 16px;
}
.post-save-panel__body--scrollable {
  overflow-y: auto;
}
.post-save-panel__section {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
}
.post-save-panel__section:first-child {
  gap: 6px;
}
.post-save-panel__section h3 {
  margin: 0;
  color: var(--gray-900, #101828);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}
.post-save-panel :deep(.share-panel--compact) {
  gap: 12px;
}
.post-save-panel
  :deep(.share-panel--compact .share-panel__link-controls .hl-input-text),
.post-save-panel
  :deep(.share-panel--compact .share-panel__link-controls .n-input) {
  height: 36px;
}
.post-save-panel
  :deep(.share-panel--compact .share-panel__link-controls .n-input) {
  --n-border: 1px solid var(--gray-300, #d0d5dd);
  border-radius: 4px;
  box-shadow: var(--shadow-xs, 0 1px 2px rgb(16 24 40 / 5%));
}
.post-save-panel :deep(.share-panel--compact .share-panel__url-input) {
  padding: 0 8px !important;
  color: var(--gray-900, #101828);
  font-size: 16px;
  line-height: 24px;
}
.post-save-panel :deep(.share-panel--compact .share-panel__icon-button),
.post-save-panel :deep(.share-panel--compact .share-panel__copy-button) {
  width: 36px;
  min-width: 36px;
  height: 36px;
  border-radius: 4px;
}
.post-save-panel :deep(.share-panel--compact .share-panel__copy-button) {
  background: var(--primary-600, #155eef);
}
.post-save-panel :deep(.share-panel--compact .share-panel__social-link) {
  width: 40px;
  height: 40px;
  padding: 8px;
  border: 1px solid var(--gray-200, #eaecf0);
  border-radius: 48px;
  background: var(--base-white, #fff);
}
.post-save-panel :deep(.share-panel--compact .share-panel__social-link svg) {
  width: 24px;
  height: 24px;
}
.post-save-panel__layouts {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}
.post-save-panel__layout {
  width: 100%;
  min-width: 0;
  height: auto !important;
  padding: 0 !important;
  color: var(--gray-600, #475467) !important;
  background: transparent !important;
}
.post-save-panel__layout :deep(.n-button__content) {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 6px;
}
.post-save-panel__layout :deep(.n-button__border),
.post-save-panel__layout :deep(.n-button__state-border) {
  display: none;
}
.post-save-panel__layout :deep(.embed-type-thumbnail--compact) {
  height: 88px;
  aspect-ratio: auto;
  border-radius: 8px;
}
.post-save-panel__layout-label {
  color: var(--gray-600, #475467);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}
.post-save-panel__workflow {
  display: flex;
  width: 100%;
  min-width: 0;
  height: auto !important;
  align-items: center;
  padding: 12px 12px 12px 16px !important;
  border: 1px solid var(--gray-200, #eaecf0);
  border-radius: 8px;
  color: var(--gray-900, #101828) !important;
  background: var(--base-white, #fff) !important;
  cursor: pointer;
  text-align: left;
}
.post-save-panel__workflow :deep(.n-button__content) {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 12px;
  text-align: left;
}
.post-save-panel__workflow-icon {
  display: grid;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  place-items: center;
  border-radius: 50%;
  color: var(--violet-600, #7a5af8);
  background: var(--violet-100, #ebe9fe);
}
.post-save-panel__workflow-icon svg {
  width: 18px;
  height: 18px;
}
.post-save-panel__workflow-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}
.post-save-panel__workflow-title {
  overflow: hidden;
  color: var(--gray-900, #101828);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.post-save-panel__workflow-subtitle {
  overflow: hidden;
  color: var(--gray-600, #475467);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.post-save-panel__workflow-chevron {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  margin-left: auto;
  color: var(--gray-700, #344054);
}
.post-save-panel__footer {
  display: flex;
  min-height: 57px;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;
  border-top: 1px solid var(--gray-200, #eaecf0);
  background: var(--base-white, #fff);
  box-sizing: border-box;
}
.post-save-panel__footer :deep(.n-checkbox__label) {
  color: var(--gray-900, #101828);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  cursor: pointer;
}
</style>
